import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["presenceContainer", "entriesBody", "entryCount", "emptyState", "editDialog", "editTitle", "editContactStatus", "editMemo", "editConfirmedSchedule", "editConfirmedScheduleEnd", "editTestSchedule", "editVenueId", "editMeetingUrl", "editMeetingId", "editMeetingPasscode"]
  static values = { selectionListId: Number, currentUserId: Number, interviewDurationMinutes: Number }

  connect() {
    this.editingEntryId = null
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { channel: "SelectionListChannel", selection_list_id: this.selectionListIdValue },
      {
        connected: () => this.startHeartbeat(),
        disconnected: () => this.stopHeartbeat(),
        received: (data) => this.handleMessage(data)
      }
    )

    // NOTE: ブラウザは非アクティブなタブの setInterval を 1 分程度まで間引くため、
    //       ハートビートだけだと在席の期限切れになる。 タブに戻った時点で即座に打ち直す。
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") this.sendHeartbeat()
    }
    document.addEventListener("visibilitychange", this.visibilityHandler)

    if (this.hasEditDialogTarget) {
      this.editDialogTarget.addEventListener("click", (e) => {
        if (e.target === this.editDialogTarget) this.closeEdit()
      })
      // close イベント (× ボタン / 保存 / バックドロップ / ESC) で確実にロックを解放
      this.editDialogTarget.addEventListener("close", () => this.handleDialogClosed())
    }
  }

  // dialog の close イベント発火後の後処理。再 open できる状態に戻す。
  handleDialogClosed() {
    if (!this.editingEntryId) return
    this.unlockEntryVisual(this.editingEntryId)
    const row = document.getElementById(`workspace-entry-${this.editingEntryId}`)
    if (row) {
      const ctrl = this.application.getControllerForElementAndIdentifier(row, "workspace-entry")
      if (ctrl && typeof ctrl.setLockState === "function") {
        ctrl.setLockState(false, null)
      }
    }
    this.editingEntryId = null
  }

  disconnect() {
    this.stopHeartbeat()
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler)
      this.visibilityHandler = null
    }
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.consumer) {
      this.consumer.disconnect()
    }
  }

  startHeartbeat() {
    this.sendHeartbeat()
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), 30000)
  }

  sendHeartbeat() {
    if (this.subscription) this.subscription.perform("heartbeat")
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  handleMessage(data) {
    switch (data.type) {
      case "presences":
        this.updatePresences(data.users)
        break
      case "lock_changed":
        this.updateLockState(data.entry_id, data.locked_by)
        break
      case "locks_released":
        this.releaseLocksForUser(data.user_id)
        break
      case "entry_added":
        this.addEntry(data)
        break
      case "entry_updated":
        this.updateEntry(data.entry_id, data.html, data.candidate_id, data.candidate_row_html)
        break
      case "entry_removed":
        this.removeEntry(data)
        break
      case "draft_updated":
        this.updateDraft(data)
        break
      case "draft_removed":
        this.removeDraft(data.candidate_id)
        break
      case "draft_lock_changed":
        this.updateDraftLockState(data.candidate_id, data.locked_by)
        break
      case "bulk_mode_changed":
        this.updateBulkModeState(data.scope, data.locked_by)
        break
      case "bulk_mode_denied":
        this.handleBulkModeDenied(data.scope, data.locked_by)
        break
      case "candidate_lock_denied":
        this.handleCandidateLockDenied(data.candidate_id, data.locked_by)
        break
      case "editors_changed":
        this.updateEditors(data.editors_by_scope || {})
        break
      case "lock_denied":
        this.handleEntryLockDenied(data)
        break
    }
  }

  // --- bulk mode ---

  enterBulkMode(scope) {
    this.subscription.perform("enter_bulk_mode", { scope })
  }

  exitBulkMode(scope) {
    this.subscription.perform("exit_bulk_mode", { scope })
  }

  updateBulkModeState(scope, lockedBy) {
    const listModeEl = document.querySelector(`[data-controller~='list-mode'][data-list-mode-scope-value='${scope}']`)
    if (!listModeEl) return
    const controller = this.application.getControllerForElementAndIdentifier(listModeEl, "list-mode")
    if (!controller) return

    const isLockedByOther = lockedBy && lockedBy.id !== this.currentUserIdValue
    if (isLockedByOther) {
      controller.applyLockFromOther(lockedBy.name)
    } else {
      controller.clearLockFromOther()
    }
  }

  updateEditors(editorsByScope) {
    // スコープごとに該当する list-mode コントローラを更新
    document.querySelectorAll("[data-controller~='list-mode']").forEach(el => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, "list-mode")
      if (!ctrl) return
      const scope = ctrl.scopeValue
      const editors = editorsByScope[scope] || []
      const others = editors.filter(e => e.id !== this.currentUserIdValue)
      const blocker = others[0] || null
      if (blocker) {
        ctrl.setBulkBlocker(blocker.name)
      } else {
        ctrl.clearBulkBlocker()
      }
    })
  }

  handleEntryLockDenied(data) {
    const name = data.locked_by?.name || "他のユーザー"
    this.showToast(`${name} が編集中のため編集できません`, "warning")
    // 対象エントリの編集モーダルが開いていれば閉じる
    if (this.editingEntryId && this.editingEntryId === data.entry_id) {
      if (this.hasEditDialogTarget) this.editDialogTarget.close()
      this.editingEntryId = null
    }
  }

  handleCandidateLockDenied(candidateId, lockedBy) {
    const name = lockedBy?.name || "他のユーザー"
    this.showToast(`${name} が一括編集中のため編集できません`, "warning")
    // 開いているモーダルを閉じる
    const modalEl = document.querySelector("[data-controller~='candidate-modal']")
    if (modalEl) {
      const modal = this.application.getControllerForElementAndIdentifier(modalEl, "candidate-modal")
      if (modal && modal.close) modal.close()
    }
  }

  handleBulkModeDenied(scope, lockedBy) {
    const listModeEl = document.querySelector(`[data-controller~='list-mode'][data-list-mode-scope-value='${scope}']`)
    if (!listModeEl) return
    const controller = this.application.getControllerForElementAndIdentifier(listModeEl, "list-mode")
    if (!controller) return
    const name = lockedBy?.name || "他のユーザー"
    this.showToast(`${name} が編集中のため、一括編集モードに切り替えられません`, "warning")
    // NOTE: 単にモードを戻すだけ。applyLockFromOther は相手が実際に bulk mode に
    // 入った時のみ（bulk_mode_changed の broadcast 経由で）呼ぶ
    controller.forceExitBulk()
  }

  showToast(message, type = "info") {
    const colorClass = type === "warning"
      ? "bg-amber-50 border-amber-300 text-amber-900"
      : type === "error"
      ? "bg-red-50 border-red-300 text-red-900"
      : "bg-white border-gray-300 text-gray-900"

    const toast = document.createElement("div")
    toast.className = `fixed top-6 right-6 z-[100] rounded-lg border px-4 py-3 shadow-lg transition-opacity duration-300 ${colorClass}`
    toast.dataset.controller = "toast"
    toast.innerHTML = `
      <div class="flex items-center gap-3">
        <svg class="w-5 h-5 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
        </svg>
        <span class="text-sm font-medium">${this.escapeHtml(message)}</span>
      </div>
    `
    document.body.appendChild(toast)
  }

  updatePresences(users) {
    if (!this.hasPresenceContainerTarget) return

    this.presenceContainerTarget.innerHTML = users.map(user =>
      `<span class="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700"><span class="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>${this.escapeHtml(user.name)} が表示中</span>`
    ).join("")
  }

  // --- エントリテーブル（上）---

  updateLockState(entryId, lockedBy) {
    const row = document.getElementById(`workspace-entry-${entryId}`)
    if (!row) return

    const isLockedByOther = lockedBy && lockedBy.id !== this.currentUserIdValue
    const entryController = this.application.getControllerForElementAndIdentifier(row, "workspace-entry")

    if (entryController) {
      entryController.setLockState(isLockedByOther, lockedBy?.name)
    }
  }

  releaseLocksForUser(userId) {
    if (userId === this.currentUserIdValue) return

    document.querySelectorAll("[data-controller~='workspace-entry']").forEach(el => {
      const controller = this.application.getControllerForElementAndIdentifier(el, "workspace-entry")
      if (controller) {
        controller.releaseLockIfUser(userId)
      }
    })

    // 候補者テーブルのロックも解放
    document.querySelectorAll("[data-candidate-id]").forEach(row => {
      this.setCandidateRowLock(row, false, null)
    })
  }

  addEntry(data) {
    if (!this.hasEntriesBodyTarget) return

    const existing = document.getElementById(`workspace-entry-${data.entry_id}`)
    if (!existing) {
      this.entriesBodyTarget.insertAdjacentHTML("beforeend", data.html)
      this.sortEntries()
      this.updateEntryCount()
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.add("hidden")
      }
    }
    this.refreshEntriesBulkButton()

    // NOTE: 候補者テーブルからは消さずグレーアウトで残す。再描画 HTML が来ていれば置き換える。
    this.replaceCandidateRow(data.candidate_id, data.candidate_row_html)
  }

  updateEntry(entryId, html, candidateId, candidateRowHtml) {
    const existing = document.getElementById(`workspace-entry-${entryId}`)
    if (existing) {
      const entryController = this.application.getControllerForElementAndIdentifier(existing, "workspace-entry")
      if (!(entryController && entryController.isEditing)) {
        existing.outerHTML = html
        this.sortEntries()
      }
    }
    // 下のパネルの候補者行もグレーアウト状態で同期する
    if (candidateId && candidateRowHtml) {
      this.replaceCandidateRow(candidateId, candidateRowHtml)
    }
  }

  removeEntry(data) {
    const row = document.getElementById(`workspace-entry-${data.entry_id}`)
    if (row) {
      row.remove()
      this.updateEntryCount()
      this.refreshEntriesBulkButton()
      // エントリ 0 件になったら空メッセージを表示
      if (this.hasEmptyStateTarget && this.hasEntriesBodyTarget && this.entriesBodyTarget.querySelectorAll("tr").length === 0) {
        this.emptyStateTarget.classList.remove("hidden")
      }
    }

    // 候補者テーブルの該当行をグレーアウト解除済みの状態で再描画する。
    this.replaceCandidateRow(data.candidate_id, data.candidate_row_html)
  }

  // 候補者行を最新の HTML で置き換える ( なければ末尾に追加してソート )。
  replaceCandidateRow(candidateId, html) {
    if (!candidateId || !html) return
    const existing = document.getElementById(`candidate-row-${candidateId}`)
    if (existing) {
      existing.outerHTML = html
    } else {
      const candidatesBody = document.getElementById("candidates-body")
      if (candidatesBody) {
        candidatesBody.insertAdjacentHTML("beforeend", html)
        this.sortCandidates(candidatesBody)
      }
    }
  }

  refreshEntriesBulkButton() {
    if (!this.hasEntriesBodyTarget) return
    const isEmpty = this.entriesBodyTarget.querySelectorAll("tr").length === 0
    // エントリパネル側の list-mode コントローラを取得
    const entriesPanelEl = this.entriesBodyTarget.closest("[data-controller~='list-mode']")
    if (!entriesPanelEl) return
    const ctrl = this.application.getControllerForElementAndIdentifier(entriesPanelEl, "list-mode")
    if (ctrl && typeof ctrl.setBulkButtonInitialDisabled === "function") {
      ctrl.setBulkButtonInitialDisabled(isEmpty)
    }
  }

  sortEntries() {
    if (!this.hasEntriesBodyTarget) return

    const rows = Array.from(this.entriesBodyTarget.querySelectorAll("tr"))
    const hasSegments = rows.some(r => r.dataset.segmentName)

    // NOTE: サーバー側 Ruby sort ( [segment_index, priority, redistribution_id, candidate_id] ) と揃える
    if (hasSegments) {
      rows.sort((a, b) => {
        const segA = a.dataset.segmentName || ""
        const segB = b.dataset.segmentName || ""
        if (segA !== segB) return segA.localeCompare(segB, "ja")
        const priA = parseInt(a.dataset.priorityValue ?? "999", 10)
        const priB = parseInt(b.dataset.priorityValue ?? "999", 10)
        if (priA !== priB) return priA - priB
        const ridA = parseInt(a.dataset.redistributionId || "0", 10) || 0
        const ridB = parseInt(b.dataset.redistributionId || "0", 10) || 0
        if (ridA !== ridB) return ridA - ridB
        return (parseInt(a.dataset.candidateId || "0", 10) || 0) - (parseInt(b.dataset.candidateId || "0", 10) || 0)
      })
    } else {
      rows.sort((a, b) => {
        const orderA = parseInt(a.dataset.statusOrder ?? "5", 10)
        const orderB = parseInt(b.dataset.statusOrder ?? "5", 10)
        if (orderA !== orderB) return orderA - orderB
        const posA = parseInt(a.dataset.position ?? "0", 10)
        const posB = parseInt(b.dataset.position ?? "0", 10)
        if (posA !== posB) return posA - posB
        return (parseInt(a.dataset.candidateId || "0", 10) || 0) - (parseInt(b.dataset.candidateId || "0", 10) || 0)
      })
    }

    let prevSegment = null
    rows.forEach(row => {
      row.classList.remove("!border-t-[3px]", "!border-t-gray-300")
      if (hasSegments) {
        const seg = row.dataset.segmentName || ""
        if (prevSegment !== null && seg !== prevSegment) {
          row.classList.add("!border-t-[3px]", "!border-t-gray-300")
        }
        prevSegment = seg
      }
      this.entriesBodyTarget.appendChild(row)
    })
  }

  updateEntryCount() {
    if (!this.hasEntryCountTarget || !this.hasEntriesBodyTarget) return
    const count = this.entriesBodyTarget.querySelectorAll("tr").length
    this.entryCountTarget.textContent = `(${count}名)`
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  sortCandidates(tbody) {
    const rows = Array.from(tbody.querySelectorAll("tr"))
    const hasPriority = rows.some(r => r.dataset.priorityValue && r.dataset.priorityValue !== "999")
    // NOTE: サーバー側の SQL sort ( CASE priority, redistribution_id ASC NULLS LAST, mid ASC, id ASC ) と揃える
    rows.sort((a, b) => {
      if (hasPriority) {
        const priA = parseInt(a.dataset.priorityValue ?? "999", 10)
        const priB = parseInt(b.dataset.priorityValue ?? "999", 10)
        if (priA !== priB) return priA - priB
      }
      const ridA = parseInt(a.dataset.redistributionId || "0", 10) || 0
      const ridB = parseInt(b.dataset.redistributionId || "0", 10) || 0
      if (ridA !== ridB) return ridA - ridB
      const midA = a.dataset.mid || ""
      const midB = b.dataset.mid || ""
      if (midA !== midB) return midA.localeCompare(midB)
      return (parseInt(a.dataset.candidateId || "0", 10) || 0) - (parseInt(b.dataset.candidateId || "0", 10) || 0)
    })
    rows.forEach(row => tbody.appendChild(row))
  }

  // --- 候補者テーブル（下）の draft 同期 ---

  updateDraft(data) {
    // NOTE: 自分の編集でもモーダル <-> 一覧行の同期が必要なため、早期 return せず
    // activeElement ガードでフォーカス中の入力の上書きだけ避ける
    const row = document.getElementById(`candidate-row-${data.candidate_id}`)
    if (row) {
      this.applyDraftToRow(row, "workspace-candidates", data)
    }

    // モーダル下部のプレビューフォームも同期
    const previewForm = document.getElementById(`candidate-preview-form-${data.candidate_id}`)
    if (previewForm) {
      this.applyDraftToRow(previewForm, "candidate-preview-form", data)
    }
  }

  applyDraftToRow(container, targetPrefix, data) {
    const select = container.querySelector(`[data-${targetPrefix}-target='contactStatus']`)
    if (select && document.activeElement !== select) select.value = data.contact_status || ""

    const input = container.querySelector(`[data-${targetPrefix}-target='memo']`)
    if (input && document.activeElement !== input) input.value = data.memo || ""

    // confirmed-schedule コントローラーがあれば setStartTime / setEndTime を使う
    const csEl = container.querySelector("[data-controller~='confirmed-schedule']")
    if (csEl) {
      const csCtrl = this.application.getControllerForElementAndIdentifier(csEl, "confirmed-schedule")
      if (csCtrl) {
        const csStart = csEl.querySelector("[data-confirmed-schedule-target='startHidden']")
        const csEnd = csEl.querySelector("[data-confirmed-schedule-target='endHidden']")
        if (!csStart || document.activeElement !== csStart) csCtrl.setStartTime(data.confirmed_schedule || "")
        if (!csEnd || document.activeElement !== csEnd) csCtrl.setEndTime(data.confirmed_schedule_end || "")
      }
    } else {
      const scheduleInput = container.querySelector(`[data-${targetPrefix}-target='confirmedSchedule']`)
      if (scheduleInput && document.activeElement !== scheduleInput) {
        this.applyDatetimePickerValue(scheduleInput, data.confirmed_schedule || "")
      }
      const scheduleEndInput = container.querySelector(`[data-${targetPrefix}-target='confirmedScheduleEnd']`)
      if (scheduleEndInput && document.activeElement !== scheduleEndInput) {
        this.applyDatetimePickerValue(scheduleEndInput, data.confirmed_schedule_end || "")
      }
    }

    const testScheduleInput = container.querySelector(`[data-${targetPrefix}-target='testSchedule']`)
    if (testScheduleInput && document.activeElement !== testScheduleInput) {
      this.applyDatetimePickerValue(testScheduleInput, data.test_schedule || "")
    }

    const venueSelect = container.querySelector(`[data-${targetPrefix}-target='venueId']`)
    if (venueSelect && document.activeElement !== venueSelect) {
      venueSelect.value = data.venue_id != null ? String(data.venue_id) : ""
      venueSelect.dispatchEvent(new Event("change", { bubbles: true }))
    }

    const meetingUrlInput = container.querySelector(`[data-${targetPrefix}-target='meetingUrl']`)
    if (meetingUrlInput && document.activeElement !== meetingUrlInput) meetingUrlInput.value = data.meeting_url || ""
    const meetingIdInput = container.querySelector(`[data-${targetPrefix}-target='meetingId']`)
    if (meetingIdInput && document.activeElement !== meetingIdInput) meetingIdInput.value = data.meeting_id || ""
    const meetingPasscodeInput = container.querySelector(`[data-${targetPrefix}-target='meetingPasscode']`)
    if (meetingPasscodeInput && document.activeElement !== meetingPasscodeInput) meetingPasscodeInput.value = data.meeting_passcode || ""

    // list-mode のテキスト表示も同期
    const listModeEl = container.closest("[data-controller~='list-mode']")
    if (listModeEl) {
      const lmCtrl = this.application.getControllerForElementAndIdentifier(listModeEl, "list-mode")
      if (lmCtrl && lmCtrl.syncFormText) lmCtrl.syncFormText()
    }
  }

  removeDraft(candidateId) {
    const row = document.getElementById(`candidate-row-${candidateId}`)
    if (row) this.clearDraftInputs(row, "workspace-candidates")

    const previewForm = document.getElementById(`candidate-preview-form-${candidateId}`)
    if (previewForm) this.clearDraftInputs(previewForm, "candidate-preview-form")
  }

  clearDraftInputs(container, targetPrefix) {
    const select = container.querySelector(`[data-${targetPrefix}-target='contactStatus']`)
    if (select) select.value = ""
    const input = container.querySelector(`[data-${targetPrefix}-target='memo']`)
    if (input) input.value = ""
    const scheduleInput = container.querySelector(`[data-${targetPrefix}-target='confirmedSchedule']`)
    if (scheduleInput) this.applyDatetimePickerValue(scheduleInput, "")
    const venueSelect = container.querySelector(`[data-${targetPrefix}-target='venueId']`)
    if (venueSelect) {
      venueSelect.value = ""
      venueSelect.dispatchEvent(new Event("change", { bubbles: true }))
    }
    const meetingUrlInput = container.querySelector(`[data-${targetPrefix}-target='meetingUrl']`)
    if (meetingUrlInput) meetingUrlInput.value = ""
    const meetingIdInput = container.querySelector(`[data-${targetPrefix}-target='meetingId']`)
    if (meetingIdInput) meetingIdInput.value = ""
    const meetingPasscodeInput = container.querySelector(`[data-${targetPrefix}-target='meetingPasscode']`)
    if (meetingPasscodeInput) meetingPasscodeInput.value = ""
  }

  updateDraftLockState(candidateId, lockedBy) {
    const isLockedByOther = lockedBy && lockedBy.id !== this.currentUserIdValue

    const row = document.getElementById(`candidate-row-${candidateId}`)
    if (row) this.setCandidateRowLock(row, isLockedByOther, lockedBy?.name)

    const previewForm = document.getElementById(`candidate-preview-form-${candidateId}`)
    if (previewForm) this.setPreviewFormLock(previewForm, isLockedByOther, lockedBy?.name)
  }

  setPreviewFormLock(container, locked, lockerName) {
    const select = container.querySelector("[data-candidate-preview-form-target='contactStatus']")
    const input = container.querySelector("[data-candidate-preview-form-target='memo']")
    const scheduleInput = container.querySelector("[data-candidate-preview-form-target='confirmedSchedule']")
    const lockInfo = container.querySelector("[data-candidate-preview-form-target='lockInfo']")

    if (locked) {
      container.classList.add("bg-gray-100")
      if (select) select.disabled = true
      if (input) input.disabled = true
      if (scheduleInput) scheduleInput.disabled = true
      if (lockInfo) {
        lockInfo.innerHTML = `
          <span class="inline-flex items-center gap-1 text-amber-600">
            <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/>
            </svg>
            ${this.escapeHtml(lockerName)}が編集中
          </span>`
      }
    } else {
      container.classList.remove("bg-gray-100")
      if (select) select.disabled = false
      if (input) input.disabled = false
      if (scheduleInput) scheduleInput.disabled = false
      if (lockInfo) lockInfo.innerHTML = ""
    }
  }

  setCandidateRowLock(row, locked, lockerName) {
    const select = row.querySelector("[data-workspace-candidates-target='contactStatus']")
    const input = row.querySelector("[data-workspace-candidates-target='memo']")
    const scheduleInput = row.querySelector("[data-workspace-candidates-target='confirmedSchedule']")
    const lockInfo = row.querySelector("[data-workspace-candidates-target='lockInfo']")

    row.dataset.rowLocked = locked ? "true" : ""
    if (locked) {
      row.classList.add("bg-gray-100", "opacity-60", "cursor-not-allowed")
      row.classList.remove("hover:bg-indigo-50", "cursor-pointer")
      if (select) select.disabled = true
      if (input) input.disabled = true
      if (scheduleInput) scheduleInput.disabled = true
      if (lockInfo) {
        lockInfo.innerHTML = `
          <span class="inline-flex items-center gap-1 text-amber-600">
            <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/>
            </svg>
            ${this.escapeHtml(lockerName)}が編集中
          </span>`
      }
    } else {
      // NOTE: 確定済み行 ( グレーアウト ) はロック解放後も disabled を維持する。
      const isConfirmedGrayed = row.dataset.confirmedGrayed === "true"
      // ロック由来の bg-gray-100 と opacity-60 のみ解除 ( 確定行の bg-gray-200 / text-gray-400 はそのまま )
      row.classList.remove("bg-gray-100", "opacity-60", "cursor-not-allowed")
      if (select) select.disabled = false
      if (input) input.disabled = isConfirmedGrayed
      if (scheduleInput) scheduleInput.disabled = isConfirmedGrayed
      if (lockInfo) lockInfo.innerHTML = ""
      // 架電モード中ならカーソルとホバーを復元 ( 確定行を除く )
      const listModeEl = row.closest("[data-controller~='list-mode']")
      if (listModeEl && !isConfirmedGrayed) {
        const ctrl = this.application.getControllerForElementAndIdentifier(listModeEl, "list-mode")
        if (ctrl && ctrl.modeValue === "call") {
          row.classList.add("cursor-pointer", "hover:bg-indigo-50")
        }
      }
    }
  }

  // --- workspace-entry / workspace-candidates から呼ばれるヘルパー ---

  lockEntry(entryId) {
    this.subscription.perform("lock_entry", { entry_id: entryId })
  }

  unlockEntry(entryId) {
    this.subscription.perform("unlock_entry", { entry_id: entryId })
  }

  lockEntryVisual(entryId) {
    this.subscription.perform("lock_entry_visual", { entry_id: entryId })
  }

  unlockEntryVisual(entryId) {
    this.subscription.perform("unlock_entry_visual", { entry_id: entryId })
  }

  lockCandidate(candidateId) {
    this.subscription.perform("lock_candidate", { candidate_id: candidateId })
  }

  unlockCandidate(candidateId) {
    this.subscription.perform("unlock_candidate", { candidate_id: candidateId })
  }

  // --- エントリ編集モーダル ---

  openEdit(entryId, candidateName, contactStatus, memo, confirmedSchedule, confirmedScheduleEnd, testSchedule, venueId, meetingUrl, meetingId, meetingPasscode) {
    if (!this.hasEditDialogTarget) return

    // すでに open のままになっている場合は一旦閉じてから開き直す（InvalidStateError 回避）
    if (this.editDialogTarget.open) {
      this.editDialogTarget.close()
    }

    this.editingEntryId = entryId
    this.editTitleTarget.textContent = `${candidateName} を編集`
    this.editContactStatusTarget.value = contactStatus || ""
    this.editMemoTarget.value = memo || ""
    this.applyDatetimePickerValue(this.editConfirmedScheduleTarget, confirmedSchedule || "")
    if (this.hasEditConfirmedScheduleEndTarget) {
      this.applyDatetimePickerValue(this.editConfirmedScheduleEndTarget, confirmedScheduleEnd || "")
    }
    if (this.hasEditTestScheduleTarget) {
      this.applyDatetimePickerValue(this.editTestScheduleTarget, testSchedule || "")
    }
    if (this.hasEditVenueIdTarget) {
      this.editVenueIdTarget.value = venueId || ""
      this.refreshVenueHint(this.editVenueIdTarget)
    }
    if (this.hasEditMeetingUrlTarget) this.editMeetingUrlTarget.value = meetingUrl || ""
    if (this.hasEditMeetingIdTarget) this.editMeetingIdTarget.value = meetingId || ""
    if (this.hasEditMeetingPasscodeTarget) this.editMeetingPasscodeTarget.value = meetingPasscode || ""
    this.editDialogTarget.showModal()
  }

  // venue-hint コントローラーを取得してヒントを更新する（イベント dispatch は使わない）
  refreshVenueHint(selectEl) {
    const wrapper = selectEl.closest("[data-controller~='venue-hint']")
    if (!wrapper) return
    const ctrl = this.application.getControllerForElementAndIdentifier(wrapper, "venue-hint")
    if (ctrl && typeof ctrl.update === "function") ctrl.update()
  }

  // hidden input 経由で datetime-picker controller に値をセットする
  applyDatetimePickerValue(hiddenInput, value) {
    hiddenInput.value = value
    const wrapper = hiddenInput.closest("[data-controller~='datetime-picker']")
    if (!wrapper) return
    const dpCtrl = this.application.getControllerForElementAndIdentifier(wrapper, "datetime-picker")
    if (dpCtrl && typeof dpCtrl.setValue === "function") {
      dpCtrl.setValue(value)
    }
  }

  // モーダルの開始時間変更 → 終了時間の日付を同期し、インタビュー時間が設定されていれば終了時刻も自動計算
  autoFillEditEndTime(event) {
    const startValue = event.target.value
    if (!startValue || !this.hasEditConfirmedScheduleEndTarget) return
    const endHidden = this.editConfirmedScheduleEndTarget
    const wrapper = endHidden.closest("[data-controller~='datetime-picker']")

    // time_only モード: date hidden input に開始と同じ日付をセット
    if (wrapper) {
      const endDateInput = wrapper.querySelector("[data-datetime-picker-target='date']")
      if (endDateInput && endDateInput.type === "hidden") {
        endDateInput.value = startValue.split("T")[0]
      }
    }

    if (!this.interviewDurationMinutesValue) return
    const endValue = this.addMinutesToDatetimeLocal(startValue, this.interviewDurationMinutesValue)
    this.applyDatetimePickerValue(endHidden, endValue)
  }

  addMinutesToDatetimeLocal(datetimeLocal, minutes) {
    const [datePart, timePart] = datetimeLocal.split("T")
    const [year, month, day] = datePart.split("-").map(Number)
    const [hour, minute] = timePart.split(":").map(Number)
    const d = new Date(year, month - 1, day, hour, minute + minutes)
    const pad = (n) => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  closeEdit() {
    if (!this.hasEditDialogTarget) return
    if (this.editDialogTarget.open) {
      this.editDialogTarget.close() // close イベントで handleDialogClosed が呼ばれる
    }
  }

  async saveEdit() {
    if (!this.editingEntryId) return

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const path = window.location.pathname
    const match = path.match(/\/admin\/projects\/(\d+)\/selection_lists\/(\d+)/)
    if (!match) return

    const url = `/admin/projects/${match[1]}/selection_lists/${match[2]}/entries/${this.editingEntryId}`

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          entry: {
            contact_status: this.editContactStatusTarget.value,
            memo: this.editMemoTarget.value,
            confirmed_schedule: this.hasEditConfirmedScheduleTarget ? this.editConfirmedScheduleTarget.value : "",
            confirmed_schedule_end: this.hasEditConfirmedScheduleEndTarget ? this.editConfirmedScheduleEndTarget.value : "",
            test_schedule: this.hasEditTestScheduleTarget ? this.editTestScheduleTarget.value : "",
            venue_id: this.hasEditVenueIdTarget ? (this.editVenueIdTarget.value || null) : null,
            meeting_url: this.hasEditMeetingUrlTarget ? this.editMeetingUrlTarget.value : "",
            meeting_id: this.hasEditMeetingIdTarget ? this.editMeetingIdTarget.value : "",
            meeting_passcode: this.hasEditMeetingPasscodeTarget ? this.editMeetingPasscodeTarget.value : ""
          }
        })
      })

      if (response.ok) {
        // NOTE: broadcast 到達を待たずレスポンスから DOM を更新する
        await this.applyResponsePayload(response)
        this.closeEdit()
      } else if (response.status === 422) {
        const data = await response.json().catch(() => null)
        showToast(data?.errors?.join(" / ") || "保存できませんでした")
      } else if (response.status === 423) {
        showToast("この候補者は他のユーザーが編集中です")
      } else {
        showToast("保存できませんでした。時間をおいて もう一度お試しください")
      }
    } catch (error) {
      console.error("Failed to save entry:", error)
      showToast("通信できませんでした。接続を確認して もう一度お試しください")
    }
  }

  async applyResponsePayload(response) {
    const contentType = response.headers.get("content-type") || ""
    if (!contentType.includes("application/json")) return
    const data = await response.json()
    if (!data || !data.type || data.type === "noop") return
    this.handleMessage(data)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
